import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { WithPluginState } from '@atlaskit/editor-common/with-plugin-state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { isResolvingMentionProvider } from '@atlaskit/mention/resource';
import { isPromise } from '@atlaskit/mention/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { openAIModal as _openAIModal } from './editor-actions/actions';
import { createAIAuditLogPlugin } from './pm-plugins/ai-audit-logs/ai-audit-log-plugin';
import { createAIButtonPlugin } from './pm-plugins/ai-button/ai-button-plugin';
import { aiButtonPluginKey } from './pm-plugins/ai-button/ai-button-plugin-key';
import { createAIEventHubPlugin } from './pm-plugins/ai-event-hub/ai-event-hub-plugin';
import { createAISmartButtonAPIPlugin } from './pm-plugins/ai-smart-button-api/ai-smart-button-api-plugin';
import { createEndAIExperienceCommand } from './pm-plugins/decoration/actions';
import { createDecorationPlugin } from './pm-plugins/decoration/decoration-plugin';
import { getPluginState } from './pm-plugins/decoration/decoration-plugin-factory';
import { aiExperienceDecorationPluginKey } from './pm-plugins/decoration/decoration-plugin-key';
import { hasGeneratedContentDecorations } from './pm-plugins/decoration/utils/hasGeneratedContentDecorations';
import { createGetEditorViewPlugin } from './pm-plugins/get-editor-view/get-editor-view-plugin';
import { createInlineSuggestionPlugin } from './pm-plugins/inline-suggestions/inline-suggestion-plugin';
import { rovoAgentsPluginKey } from './pm-plugins/rovo-agents/plugin-key';
import { createRovoAgentsPlugin } from './pm-plugins/rovo-agents/rovo-agents-plugin';
import { createSelectionPreviewPlugin } from './pm-plugins/selection-preview/selection-preview-plugin';
import { createSelectionPlugin } from './pm-plugins/selection/selection-plugin';
import { selectionPluginKey as aiExperienceSelectionPluginKey } from './pm-plugins/selection/selection-plugin-key';
import { AgentFetcher } from './ui/components/AgentFetcher/AgentFetcher';
import { LoadableBrowseRovoAgentsModal } from './ui/components/BrowseRovoAgentsModal/BrowseRovoAgentsModal';
import { ContentComponentWrapper } from './ui/components/ContentComponent/ContentComponentWrapper';
import { ExperienceApplication } from './ui/components/ExperienceApplication/ExperienceApplication';
import { ModalRegionErrorBoundary } from './ui/components/ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { GlobalWrapper } from './ui/components/GlobalWrapper/GlobalWrapper';
import { ModalRegion } from './ui/components/ModalRegion/ModalRegion';
import { PublishToRovo } from './ui/components/PublishToRovo/PublishToRovo';
import { RovoEnabled } from './ui/components/RovoEnabled/RovoEnabled';
import { SubscribeToRovo } from './ui/components/SubscribeToRovo/SubscribeToRovo';
import { getVisibleEditorPluginAIConfigItems } from './ui/prebuilt/config-items/config-items';
import { mapConfigItemsToQuickInsertItems } from './ui/prebuilt/config-items/utils/map-config-items-to-quick-insert-items';
import { deriveProactiveAIApiUrl } from './ui/prebuilt/utils';
import { getFloatingToolbarConfig } from './ui/toolbars/floating-toolbar';
import { getAIPrimaryToolbarComponent } from './ui/toolbars/primary-toolbar';
import { getPrimaryToolbarLegacyComponent } from './ui/toolbars/primary-toolbar-legacy';
import { getSelectionToolbarConfig } from './ui/toolbars/selection-toolbar';
import { getAIHighlightPositions } from './ui/utils/selection';
import { aiActorCache } from './ui/utils/xstate/ai-actor-cache';

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- Ignored via go/ED-25883
/**
 * @deprecated
 *
 * `NextEditorPluginAI` is deprecated and will be removed in a future release.
 * Please use the `aiPlugin` export instead.
 *
 * This temporary export exists to facilitate a smooth transition to the new plugin naming convention.
 *
 * @example
 * ```ts
 * // Old import
 * import { NextEditorPluginAI } from '@atlassian/editor-plugin-ai/EditorPluginAI';
 *
 * // New import
 * import { aiPlugin } from '@atlassian/editor-plugin-ai';
 * ```
 */
export var NextEditorPluginAI = function NextEditorPluginAI(_ref) {
  var config = _ref.config,
    api = _ref.api;
  var editorPluginAIProvider = config;
  var aiGlobalOptIn = config.aiGlobalOptIn;
  var editorView;
  var setEditorView = function setEditorView(newEditorView) {
    editorView = newEditorView;
  };
  var providerFactory;
  var setProviderFactory = function setProviderFactory(newProviderFactory) {
    providerFactory = newProviderFactory;
  };
  var mentionProvider;

  /**
   * Helper function to retrieve the name details for a mention from the mentionProvider
   */
  var getMentionNameDetails = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(id) {
      var nameDetails, mention;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!(!mentionProvider || !isResolvingMentionProvider(mentionProvider))) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return");
          case 2:
            nameDetails = mentionProvider.resolveMentionName(id);
            if (!isPromise(nameDetails)) {
              _context.next = 8;
              break;
            }
            _context.next = 6;
            return nameDetails;
          case 6:
            mention = _context.sent;
            return _context.abrupt("return", mention);
          case 8:
            return _context.abrupt("return", nameDetails);
          case 9:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function getMentionNameDetails(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var setMentionProvider = function setMentionProvider() {
    var _handler = function handler(_name, providerPromise) {
      if (providerPromise) {
        providerPromise.then(function (provider) {
          mentionProvider = provider;
          providerFactory.unsubscribe('mentionProvider', _handler);
        });
      }
    };
    providerFactory.subscribe('mentionProvider', _handler);
  };
  var setMediaProvider = function setMediaProvider() {
    var _editorPluginAIProvid;
    if (editorPluginAIProvider !== null && editorPluginAIProvider !== void 0 && (_editorPluginAIProvid = editorPluginAIProvider.providers) !== null && _editorPluginAIProvid !== void 0 && _editorPluginAIProvid.mediaProvider) {
      providerFactory.setProvider('mediaProvider', Promise.resolve(editorPluginAIProvider.providers.mediaProvider));
    }
  };
  var setEmojiProvider = function setEmojiProvider() {
    var _api$emoji;
    var _ref3 = (api === null || api === void 0 || (_api$emoji = api.emoji) === null || _api$emoji === void 0 ? void 0 : _api$emoji.sharedState.currentState()) || {},
      emojiProvider = _ref3.emojiProvider;
    if (!!emojiProvider) {
      providerFactory.setProvider('emojiProvider', Promise.resolve(emojiProvider));
    } else {
      var _api$emoji2;
      var unsub = api === null || api === void 0 || (_api$emoji2 = api.emoji) === null || _api$emoji2 === void 0 ? void 0 : _api$emoji2.sharedState.onChange(function (sharedState) {
        var _sharedState$nextShar;
        if (!!((_sharedState$nextShar = sharedState.nextSharedState) !== null && _sharedState$nextShar !== void 0 && _sharedState$nextShar.emojiProvider)) {
          providerFactory.setProvider('emojiProvider', Promise.resolve(sharedState.nextSharedState.emojiProvider));
          unsub === null || unsub === void 0 || unsub();
        }
      });
    }
  };
  var aiOptIn = aiGlobalOptIn || {
    status: 'enabled',
    triggerOptInFlow: function triggerOptInFlow() {}
  };
  var quickInsert = function quickInsert(intl) {
    var configItems = [];
    if (!editorPluginAIProvider.disableQuickInsert) {
      configItems.push.apply(configItems, [editorPluginAIProvider.baseGenerate].concat(_toConsumableArray(getVisibleEditorPluginAIConfigItems({
        configItemWithOptions: editorPluginAIProvider.configItemWithOptions,
        // The editor view will always be available when quick insert items are being created
        // Ignored via go/ees005
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        editorView: editorView,
        positions: [0, 0],
        isEmpty: true
      }))));
    }
    var quickInsertItems = mapConfigItemsToQuickInsertItems({
      configItems: configItems,
      formatMessage: intl.formatMessage,
      aiGlobalOptIn: aiOptIn,
      // The editor view will always be available when quick insert items are being created
      // Ignored via go/ees005
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      editorView: editorView
    });
    return quickInsertItems;
  };
  quickInsert.disableMemo = true;
  if (!(editorPluginAIProvider !== null && editorPluginAIProvider !== void 0 && editorPluginAIProvider.hidePrimaryToolbarButton)) {
    var _api$primaryToolbar;
    api === null || api === void 0 || (_api$primaryToolbar = api.primaryToolbar) === null || _api$primaryToolbar === void 0 || _api$primaryToolbar.actions.registerComponent({
      name: 'aiExperience',
      component: getAIPrimaryToolbarComponent({
        api: api,
        editorPluginAIProvider: editorPluginAIProvider,
        aiGlobalOptIn: aiOptIn
      })
    });
  }
  return {
    name: 'aiExperience',
    pmPlugins: function pmPlugins() {
      var plugins = [{
        name: 'ai-experience-actor',
        plugin: function plugin() {
          return new SafePlugin({
            view: function view(editorView) {
              // This sets up an [actor](https://xstate.js.org/docs/guides/actors.html#actor-api) using
              // the editor-plugin-ai machine and starts it up.
              var editorPluginAIActor = aiActorCache.getActor(editorView);
              return {
                destroy: function destroy() {
                  // This cleansup any listeners from the actor -- to ensure we don't leak memory.
                  editorPluginAIActor === null || editorPluginAIActor === void 0 || editorPluginAIActor.stop();
                  aiActorCache.delete(editorView);
                }
              };
            }
          });
        }
      }, {
        name: 'aiExperience',
        plugin: function plugin(options) {
          var payload = {
            action: 'init',
            actionSubject: 'editor',
            actionSubjectId: 'editorPluginAI',
            eventType: EVENT_TYPE.TRACK,
            attributes: {
              aiGlobalOptIn: aiOptIn === null || aiOptIn === void 0 ? void 0 : aiOptIn.status
            }
          };
          // @ts-expect-error - This prevent type errors when trying to pass EditorPluginAI payload to AnalyticsEventPayload
          options.dispatchAnalyticsEvent(payload);
          return createDecorationPlugin({
            dispatchAnalyticsEvent: options.dispatchAnalyticsEvent,
            dispatch: options.dispatch,
            api: api
          });
        }
      }, {
        name: 'ai-experience-get-editor-view-and-provider-factory',
        plugin: function plugin(options) {
          setProviderFactory(options.providerFactory);
          setMentionProvider();
          setMediaProvider();
          setEmojiProvider();
          return createGetEditorViewPlugin({
            setEditorView: setEditorView
          });
        }
      }, {
        name: 'ai-experience-ai-button',
        plugin: function plugin(options) {
          return createAIButtonPlugin({
            dispatch: options.dispatch
          });
        }
      }, {
        name: 'ai-experience-selection-preview',
        plugin: function plugin() {
          return createSelectionPreviewPlugin();
        }
      }];
      plugins.push({
        name: 'ai-event-hub',
        plugin: function plugin(_ref4) {
          var getIntl = _ref4.getIntl;
          return createAIEventHubPlugin({
            aiGlobalOptIn: aiOptIn,
            editorPluginAIProvider: editorPluginAIProvider,
            getIntl: getIntl,
            api: api
          });
        }
      });
      if (editorPluginAIProvider.isRovoEnabled) {
        plugins.push({
          name: 'editor-ai-rovo-agents',
          plugin: function plugin(options) {
            return createRovoAgentsPlugin(options);
          }
        });
        plugins.push({
          name: 'ai-experience-selection',
          plugin: function plugin() {
            return createSelectionPlugin();
          }
        });
      }
      if (editorPluginAIProvider.auditLogSettings && editorPluginAIProvider.auditLogSettings.enabled && fg('platform_editor_ai_audit_logs_events')) {
        var eventData = editorPluginAIProvider.auditLogSettings.eventData;
        plugins.push({
          name: 'ai-audit-log',
          plugin: function plugin(_ref5) {
            var dispatch = _ref5.dispatch;
            return createAIAuditLogPlugin({
              dispatch: dispatch,
              eventData: eventData
            });
          }
        });
      }
      var aiSmartButtonOptions = editorPluginAIProvider.aiSmartButtonOptions,
        dynamicSurfaceConfigItems = editorPluginAIProvider.dynamicSurfaceConfigItems;
      if (aiSmartButtonOptions && dynamicSurfaceConfigItems && fg('ai-smart-button-team-25')) {
        plugins.push({
          name: 'ai-smart-button-api',
          plugin: function plugin(_ref6) {
            var getIntl = _ref6.getIntl,
              dispatch = _ref6.dispatch;
            return createAISmartButtonAPIPlugin({
              aiGlobalOptIn: aiOptIn,
              aiSmartButtonOptions: aiSmartButtonOptions,
              api: api,
              dispatch: dispatch,
              dynamicSurfaceConfigItems: dynamicSurfaceConfigItems,
              getIntl: getIntl,
              proactiveAIApiUrl: deriveProactiveAIApiUrl(),
              product: editorPluginAIProvider.product,
              getMentionNameDetails: getMentionNameDetails
            });
          }
        });
      }
      if (editorExperiment('editor_ai_inline_suggestion_date_v2', 'test', {
        exposure: true
      })) {
        plugins.push({
          name: 'inline-suggestions',
          plugin: function plugin(_ref7) {
            var dispatch = _ref7.dispatch,
              getIntl = _ref7.getIntl;
            return createInlineSuggestionPlugin({
              dispatch: dispatch,
              getIntl: getIntl,
              api: api
            });
          }
        });
      }
      return plugins;
    },
    actions: {
      openAIModal: function openAIModal(_ref8) {
        var invokedFrom = _ref8.invokedFrom;
        _openAIModal({
          invokedFrom: invokedFrom,
          editorView: editorView,
          aiOptIn: aiOptIn,
          configItem: editorPluginAIProvider.baseGenerate
        });
      }
    },
    getSharedState: function getSharedState(state) {
      var _ref9, _aiExperienceSelectio, _aiButtonPluginKey$ge, _aiButtonPluginKey$ge2, _modalDecorationSet, _aiExperienceDecorati, _aiExperienceDecorati2, _aiExperienceDecorati3, _aiExperienceDecorati4, _aiExperienceDecorati5, _aiExperienceDecorati6, _aiExperienceDecorati7, _aiExperienceDecorati8, _aiExperienceDecorati9, _rovoAgentsPluginKey$, _isBrowseModalOpen, _rovoAgentsPluginKey$2;
      if (!state) {
        return {
          nonCursorSelectionChangeCount: 0,
          aiButtonState: {
            triggeredFor: undefined,
            isSelectionValidForAIExperience: true
          },
          aiDecorationExperiencePluginState: {
            modalDecorationSet: DecorationSet.empty,
            configItem: undefined,
            lastTriggeredFrom: undefined,
            modalMountedTimeStamp: undefined,
            triggerMethod: undefined,
            initialPrompt: undefined,
            triggeredFor: undefined,
            initialPositions: undefined,
            nudgeMetrics: undefined
          },
          rovoAgentsPluginState: {
            positions: undefined,
            isBrowseModalOpen: false
          }
        };
      }
      return {
        nonCursorSelectionChangeCount: (_ref9 = (_aiExperienceSelectio = aiExperienceSelectionPluginKey.getState(state)) !== null && _aiExperienceSelectio !== void 0 ? _aiExperienceSelectio : {}) === null || _ref9 === void 0 ? void 0 : _ref9.nonCursorSelectionChangeCount,
        aiButtonState: {
          triggeredFor: ((_aiButtonPluginKey$ge = aiButtonPluginKey.getState(state)) !== null && _aiButtonPluginKey$ge !== void 0 ? _aiButtonPluginKey$ge : {}).triggeredFor,
          isSelectionValidForAIExperience: ((_aiButtonPluginKey$ge2 = aiButtonPluginKey.getState(state)) !== null && _aiButtonPluginKey$ge2 !== void 0 ? _aiButtonPluginKey$ge2 : {}).isSelectionValidForAIExperience
        },
        aiDecorationExperiencePluginState: {
          modalDecorationSet: (_modalDecorationSet = ((_aiExperienceDecorati = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati !== void 0 ? _aiExperienceDecorati : {}).modalDecorationSet) !== null && _modalDecorationSet !== void 0 ? _modalDecorationSet : DecorationSet.empty,
          configItem: ((_aiExperienceDecorati2 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati2 !== void 0 ? _aiExperienceDecorati2 : {}).configItem,
          lastTriggeredFrom: ((_aiExperienceDecorati3 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati3 !== void 0 ? _aiExperienceDecorati3 : {}).lastTriggeredFrom,
          modalMountedTimeStamp: ((_aiExperienceDecorati4 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati4 !== void 0 ? _aiExperienceDecorati4 : {}).modalMountedTimeStamp,
          triggerMethod: ((_aiExperienceDecorati5 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati5 !== void 0 ? _aiExperienceDecorati5 : {}).triggerMethod,
          initialPrompt: ((_aiExperienceDecorati6 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati6 !== void 0 ? _aiExperienceDecorati6 : {}).initialPrompt,
          triggeredFor: ((_aiExperienceDecorati7 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati7 !== void 0 ? _aiExperienceDecorati7 : {}).triggeredFor,
          initialPositions: ((_aiExperienceDecorati8 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati8 !== void 0 ? _aiExperienceDecorati8 : {}).initialPositions,
          nudgeMetrics: ((_aiExperienceDecorati9 = aiExperienceDecorationPluginKey.getState(state)) !== null && _aiExperienceDecorati9 !== void 0 ? _aiExperienceDecorati9 : {}).nudgeMetrics
        },
        rovoAgentsPluginState: {
          positions: ((_rovoAgentsPluginKey$ = rovoAgentsPluginKey.getState(state)) !== null && _rovoAgentsPluginKey$ !== void 0 ? _rovoAgentsPluginKey$ : {}).positions,
          isBrowseModalOpen: (_isBrowseModalOpen = ((_rovoAgentsPluginKey$2 = rovoAgentsPluginKey.getState(state)) !== null && _rovoAgentsPluginKey$2 !== void 0 ? _rovoAgentsPluginKey$2 : {}).isBrowseModalOpen) !== null && _isBrowseModalOpen !== void 0 ? _isBrowseModalOpen : false
        }
      };
    },
    pluginsOptions: {
      selectionToolbar: getSelectionToolbarConfig({
        aiGlobalOptIn: aiOptIn,
        editorPluginAIProvider: editorPluginAIProvider,
        api: api
      }),
      quickInsert: quickInsert,
      floatingToolbar: getFloatingToolbarConfig({
        aiGlobalOptIn: aiOptIn,
        editorPluginAIProvider: editorPluginAIProvider,
        api: api
      })
    },
    contentComponent: function contentComponent(_ref10) {
      var editorView = _ref10.editorView,
        wrapperElement = _ref10.wrapperElement,
        appearance = _ref10.appearance,
        providerFactory = _ref10.providerFactory;
      if (fg('platform_editor_ai_composable_editor_migration')) {
        return /*#__PURE__*/React.createElement(ContentComponentWrapper, {
          aiOptIn: aiOptIn,
          api: api,
          editorView: editorView,
          wrapperElement: wrapperElement,
          appearance: appearance,
          editorPluginAIProvider: editorPluginAIProvider,
          providerFactory: providerFactory,
          getMentionNameDetails: getMentionNameDetails
        });
      }
      var endExperience = function endExperience(options) {
        if (editorPluginAIProvider.onAIProviderChanged) {
          editorPluginAIProvider.onAIProviderChanged('command-palette');
        }
        var state = getPluginState(editorView.state);
        var decoSet = hasGeneratedContentDecorations(state) || options !== null && options !== void 0 && options.preserveEditorSelectionOnComplete ? state.modalDecorationSet : undefined;
        var endAIExperienceCommand = createEndAIExperienceCommand(decoSet, options === null || options === void 0 ? void 0 : options.preserveEditorSelectionOnComplete);
        endAIExperienceCommand(editorView.state, editorView.dispatch);
        editorView.focus();
      };

      // We use this point to render the modal if needed instead of decorators
      // The reason this is preferrable is it has access to mount points
      // where decorators are rendered with a mount point within the Editor content
      // and longer modals get clipped as a result.
      var renderModalRegion = function renderModalRegion(aiExperienceDecorationPluginState) {
        var _api$engagementPlatfo;
        // If there are no active decorations -- then we don't want to render the ai experience.
        if (!aiExperienceDecorationPluginState || aiExperienceDecorationPluginState.modalDecorationSet === DecorationSet.empty) {
          return null;
        }
        if (hasGeneratedContentDecorations(aiExperienceDecorationPluginState)) {
          return null;
        }
        var modalDecorationElement = (editorView === null || editorView === void 0 ? void 0 : editorView.dom.querySelector('.ai-modal-end-widget')) || (editorView === null || editorView === void 0 ? void 0 : editorView.dom.querySelector('.ai-selection-node'));

        // This protects against cases where there is an active decoration
        // but the document has not been updated to include it
        if (!modalDecorationElement) {
          return null;
        }

        // Prioritise getting position from inlineDecoration,
        // otherwise get decoration position from start or end widget
        var positions;
        var decoration = aiExperienceDecorationPluginState.modalDecorationSet.find(undefined, undefined, function (spec) {
          return spec.key === 'inlineDecoration';
        }).shift() || aiExperienceDecorationPluginState.modalDecorationSet.find(undefined, undefined, function (spec) {
          return spec.key === 'startWidgetDecoration' || spec.key === 'endWidgetDecoration';
        }).shift();
        if (decoration) {
          positions = [decoration.from, decoration.to];
        } else {
          var nodeDecorations = aiExperienceDecorationPluginState.modalDecorationSet.find(undefined, undefined, function (spec) {
            return spec.key === 'ai-nodeDecoration';
          });
          if (nodeDecorations.length > 0) {
            var startPos = Math.min.apply(Math, _toConsumableArray(nodeDecorations.map(function (decoration) {
              return decoration.from;
            })));
            var endPos = Math.max.apply(Math, _toConsumableArray(nodeDecorations.map(function (decoration) {
              return decoration.to;
            })));
            positions = [startPos, endPos];
          }
          decoration = nodeDecorations.shift();
        }
        if (!decoration) {
          return null;
        }
        var _getPluginState = getPluginState(editorView.state),
          initialPositions = _getPluginState.initialPositions;
        return /*#__PURE__*/React.createElement(ModalRegion, {
          lastTriggeredFrom: aiExperienceDecorationPluginState.lastTriggeredFrom,
          key: aiExperienceDecorationPluginState.modalMountedTimeStamp,
          decoration: decoration,
          modalDecorationElement: modalDecorationElement,
          editorView: editorView,
          editorRelativeWrapper: wrapperElement === null || wrapperElement === void 0 ? void 0 : wrapperElement.offsetParent,
          endExperience: endExperience,
          appearance: appearance,
          decorationSet: aiExperienceDecorationPluginState.modalDecorationSet
        }, aiExperienceDecorationPluginState.configItem && editorView && /*#__PURE__*/React.createElement(ExperienceApplication, {
          configItem: aiExperienceDecorationPluginState.configItem,
          editorPluginAIProvider: editorPluginAIProvider,
          endExperience: endExperience
          // Ignored via go/ees005
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ,
          positions: positions,
          editorView: editorView,
          providerFactory: providerFactory,
          lastTriggeredFrom: aiExperienceDecorationPluginState.lastTriggeredFrom,
          nudgeMetrics: aiExperienceDecorationPluginState.nudgeMetrics,
          triggerMethod: aiExperienceDecorationPluginState.triggerMethod,
          appearance: appearance,
          initialPrompt: aiExperienceDecorationPluginState === null || aiExperienceDecorationPluginState === void 0 ? void 0 : aiExperienceDecorationPluginState.initialPrompt,
          getMentionNameDetails: getMentionNameDetails,
          triggeredFor: aiExperienceDecorationPluginState.triggeredFor,
          engagementPlatformApi: api === null || api === void 0 || (_api$engagementPlatfo = api.engagementPlatform) === null || _api$engagementPlatfo === void 0 ? void 0 : _api$engagementPlatfo.actions,
          initialPositions: initialPositions
        }));
      };
      var pluginsToTrack = {
        aiExperienceDecorationPluginState: aiExperienceDecorationPluginKey,
        rovoAgentsPluginState: rovoAgentsPluginKey
      };
      var renderedSubscribeToRovo = /*#__PURE__*/React.createElement(WithPluginState, {
        editorView: editorView,
        plugins: {
          aiExperienceDecorationPluginState: aiExperienceDecorationPluginKey
        },
        render: function render(_ref11) {
          var aiExperienceDecorationPluginState = _ref11.aiExperienceDecorationPluginState;
          var positions = getAIHighlightPositions({
            modalDecorationSet: aiExperienceDecorationPluginState === null || aiExperienceDecorationPluginState === void 0 ? void 0 : aiExperienceDecorationPluginState.modalDecorationSet
          });
          return /*#__PURE__*/React.createElement(SubscribeToRovo, {
            positions: positions,
            editorView: editorView,
            product: editorPluginAIProvider.product,
            onDocChangeByAgent: editorPluginAIProvider.onDocChangeByAgent,
            onAIProviderChanged: editorPluginAIProvider.onAIProviderChanged,
            editorPluginAIProvider: editorPluginAIProvider
          });
        }
      });
      return /*#__PURE__*/React.createElement(GlobalWrapper, null, /*#__PURE__*/React.createElement(RovoEnabled, {
        editorView: editorView,
        editorPluginAIProvider: editorPluginAIProvider
      }, /*#__PURE__*/React.createElement(PublishToRovo, {
        editorView: editorView,
        getMentionNameDetails: getMentionNameDetails,
        editorPluginAIProvider: editorPluginAIProvider,
        editorApi: api
      }), /*#__PURE__*/React.createElement(AgentFetcher, {
        editorPluginAIProvider: editorPluginAIProvider,
        editorView: editorView
      }), renderedSubscribeToRovo), /*#__PURE__*/React.createElement(WithPluginState, {
        plugins: pluginsToTrack,
        render: function render(_ref12) {
          var aiExperienceDecorationPluginState = _ref12.aiExperienceDecorationPluginState,
            rovoAgentsPluginState = _ref12.rovoAgentsPluginState;
          // The WithPluginState function does not support typing the plugin states it returns
          var modalRegion = renderModalRegion(aiExperienceDecorationPluginState);
          return /*#__PURE__*/React.createElement(ModalRegionErrorBoundary, {
            onCloseFallback: endExperience
          }, modalRegion !== null && modalRegion !== void 0 ? modalRegion : null, (rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.isBrowseModalOpen) && /*#__PURE__*/React.createElement(LoadableBrowseRovoAgentsModal, {
            editorView: editorView,
            aiGlobalOptIn: aiOptIn,
            editorPluginAIProvider: editorPluginAIProvider,
            startingConfig: aiExperienceDecorationPluginState.configItem,
            positions: rovoAgentsPluginState === null || rovoAgentsPluginState === void 0 ? void 0 : rovoAgentsPluginState.positions
          }));
        }
      }));
    },
    primaryToolbarComponent: !(api !== null && api !== void 0 && api.primaryToolbar) && !editorPluginAIProvider.hidePrimaryToolbarButton ? getPrimaryToolbarLegacyComponent({
      api: api,
      editorPluginAIProvider: editorPluginAIProvider,
      aiGlobalOptIn: aiOptIn
    }) : undefined
  };
};

// Ignored via go/ees007
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
/**
 * TODO: Remove this when making a major change as this was originally exported here
 * (Probably when deprecating the legacy plugin)
 * This type is currently used in some products.
 */
// eslint-disable-next-line @atlaskit/editor/no-re-export